import React, { useEffect, useRef } from 'react';

type LegalPersonInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const LegalPersonInfoModal: React.FC<LegalPersonInfoModalProps> = ({
  isOpen,
  onClose,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const getFocusable = () => Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]',
    ) || []);
    getFocusable()[0]?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
      if (event.key === 'Tab') {
        const focusable = getFocusable();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (!first) {
          event.preventDefault();
          dialogRef.current?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!dialogRef.current?.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus({ preventScroll: true });
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="legal-modal-backdrop"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        aria-labelledby="legal-modal-title"
        aria-modal="true"
        className="legal-modal"
        role="dialog"
        tabIndex={-1}
      >
        <div className="legal-modal-header">
          <h2
            id="legal-modal-title"
          >
            Информация о юридическом лице
          </h2>
          <button
            aria-label="Закрыть модальное окно"
            className="legal-modal-close"
            onClick={onClose}
            type="button"
          >
            ✕
          </button>
        </div>

        <div className="legal-modal-body">
          <div>
            <p className="font-semibold">Полное наименование:</p>
            <p>
              ОБЩЕСТВО С ОГРАНИЧЕННОЙ ОТВЕТСТВЕННОСТЬЮ «А2СОФТ»
            </p>
          </div>

          <div>
            <p className="font-semibold">Адрес юридического лица:</p>
            <p>
              420124, Республика Татарстан (Татарстан), г. Казань, ул. Сибгата Хакима, д. 15, кв. 2
            </p>
          </div>

          <div>
            <p className="font-semibold">ИНН:</p>
            <p>1657249187</p>
          </div>

          <div>
            <p className="font-semibold">Основной код ОКВЭД:</p>
            <p>
              <span className="font-semibold">62.01</span>
              {' — '}
              Разработка компьютерного программного обеспечения
            </p>
          </div>

          <hr className="border-slate-200" />

          <div>
            <h3 className="mb-1 text-sm font-semibold">
              Коды видов деятельности в области ИТ:
            </h3>

            <div className="space-y-3">
              <p>
                <span className="font-semibold">1.01 </span>
                <span className="italic">
                  – Проектирование, и (или) обследование, и (или) разработка,
                  и (или) адаптация, и (или) модификация (в том числе локализация,
                  кастомизация, доработка), и (или) обратное проектирование
                  (реверсивный инжиниринг), и (или) модернизация, и (или) обновление,
                  и (или) установка, и (или) интеграция, и (или) настройка,
                  и (или) конфигурирование, и (или) внедрение, и (или) сопровождение,
                  и (или) тестирование, и (или) испытания, и (или) техническая поддержка,
                  и (или) эксплуатация, включая администрирование,
                  а также оказание услуг (в том числе консультационных, услуг по обучению,
                  экспертных услуг и иных) в указанных видах деятельности (далее - проектирование
                  и (или) иная деятельность, а также оказание услуг),
                  в отношении программ для электронных вычислительных машин
                  (далее - программы для ЭВМ), и (или) баз данных (в том числе их обновлений и исправлений),
                  и (или) визуальных пользовательских интерфейсов.
                </span>
              </p>

              <p>
                <span className="font-semibold">1.05 </span>
                <span className="italic">
                  – Проектирование и (или) иная деятельность, а также оказание услуг в отношении сайтов
                  или страниц сайтов в информационно-телекоммуникационной сети,
                  включая информационно-телекоммуникационную сеть
                  {' "'}
                  Интернет
                  {'". '}
                </span>
              </p>
            </div>
          </div>

          <hr className="border-slate-200" />

          <div>
            <p>
              <span className="font-semibold">Телефон:</span>
              {' '}
              +7 (995) 095-87-00
            </p>
            <p>
              <span className="font-semibold">Email:</span>
              {' '}
              <a
                className="text-blue-600 hover:underline"
                href="mailto:info@a2soft.ru"
              >
                info@a2soft.ru
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalPersonInfoModal;
